import { limitsStatusContract2Document } from '../limits-contract2.ts';
import type {
  FetchMatcher,
  JsonApiDoc,
  JsonApiResource,
  MatcherFixture,
} from 'tio-data/demo/types';
import {
  assertNever,
  demoEmail,
  matchesSearchEndpoint,
  type DemoFixtureIdentity,
} from 'tio-employee/demo/types';
import {
  getDemoTaProgramsForClient,
  DEMO_TA_PRIMARY_TEMPLATE_ID,
  DEMO_TA_PROGRAM_CODES,
  DEMO_TAS_ANNUAL_LIMIT_DOLLARS,
  pinToAnnualLimitIfNonzero,
  type DemoTaProgram,
  type DemoTaPaymentModelId,
  type DemoTasScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

/**
 * In-progress TAS fixtures share the selected demo identity and payment model across participant/admin views.
 * JSON:API getters require unsuffixed CARD_PRE_APPROVED_TOTAL; the Integer fields belong to search hits.
 * Monetary values are cents, card balances use amounts.course_cash, and expense rows use linked course fields.
 */

// The wizard renders an input only when the template's field config marks it
// visible; these mirror a real LAUNCHED template's configs verbatim.
const wizardField = (label: string, province: string, visible: boolean, required: boolean) => ({
  values: [],
  value_as_tas_field_option: false,
  label,
  province,
  visible,
  required,
});
const WIZARD_FIELD_CONFIGS = {
  NAME: wizardField('Name', 'APPLICANT', true, true),
  EMPLOYEE_ID: wizardField('Employee ID', 'APPLICANT', true, true),
  FT_PT_OTHER: wizardField('Employee Status', 'APPLICANT', true, true),
  EMPLOYEE_DOB: wizardField('Birthday', 'APPLICATION', false, false),
  EMPLOYEE_EMAIL: wizardField('Email', 'APPLICANT', true, true),
  SCHOOL_INSTITUTION_NAME: wizardField('Institution Name', 'PROGRAM', true, true),
  STUDENT_ID: wizardField('Student ID', 'APPLICATION', false, false),
  PROGRAM_SPECIALIZATION: wizardField('Program Specialization', 'PROGRAM', true, true),
  PROGRAM_MAJOR: wizardField('Major', 'PROGRAM', false, false),
  ESTIMATED_COST: wizardField('Estimated Cost', 'PROGRAM', true, false),
  ENROLLMENT_OBJECTIVE: wizardField('Enrollment Objective', 'PROGRAM', true, false),
  ESTIMATED_PROGRAM_BEGIN: wizardField('Estimated Program Begin Date', 'PROGRAM', true, true),
  ESTIMATED_PROGRAM_COMPLETION: wizardField(
    'Estimated Program Completion Date',
    'PROGRAM',
    true,
    true
  ),
};

// One ACTIVE eligibility per catalog code — the eligibility maps and the
// participant's tas-eligibilities must cover EVERY program or the dashboard
// filters that program's card off.
const ELIGIBILITY_RESOURCES: JsonApiResource[] = DEMO_TA_PROGRAM_CODES.map((code, index) => ({
  type: 'tas-eligibility',
  id: `demo-tas-eligibility-${index + 1}`,
  attributes: { code, state: 'TAS.EligibilityState.ACTIVE' },
}));

function buildTemplateResource(program: DemoTaProgram): JsonApiResource {
  return {
    type: 'tas-program-template',
    id: program.id,
    attributes: {
      code: program.code,
      state: 'TAS.ProgramTemplateState.LAUNCHED',
      fields: {
        ...WIZARD_FIELD_CONFIGS,
        PROGRAM_NAME: { values: [program.name] },
        PROGRAM_DESCRIPTION: { values: [program.description] },
        TYPE: { values: [program.typeClassification] },
        DEPENDENT_PROGRAM: { values: [false] },
        MULTIPLE_INSTANCE_PROGRAM: { values: [false] },
        ELIGIBILITY_WAITING_PERIOD: { values: [0] },
        ...(program.isDirectPayProgram
          ? {
              // CourseCa$h™ (direct-pay) program: the same field pair a live
              // prepaid template carries — drives isDirectPayProgram /
              // isPrepaidProgram and the CourseCash chips.
              PROGRAM_PAYMENT: { values: ['ON_ATTEND'] },
              PAYMENT_TYPE_FOR_DIRECT_PAY: { values: ['TAS.AssistanceManner.COURSE_CASH'] },
            }
          : {}),
      },
      // Sales-facing program FAQs (rendered by TasFaqs on getting-started,
      // applications/show, and programs/details).
      faqs: program.faqs,
      customFields: [],
      approverInstructions: [],
      isTrial: false,
    },
    relationships: {
      conditions: { data: [] },
      tasEligibilities: { data: [] },
      tasGroupings: { data: [] },
      tasProgramInstances: { data: [] },
      tasFieldOptions: { data: [] },
      tasGroups: { data: [] },
      // Admin application/template screens read template.company.companySetting.
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

/** demo-tas-template-N → demo-tas-instance-N (stable per-program instance ids). */
function instanceIdFor(templateId: string): string {
  return templateId.replace('template', 'instance');
}

// Instances the presenter submitted THIS page load. Fixtures rebuild per
// request, so the next read serves PENDING_PROGRAM_APPROVAL and submit lands
// on the program page instead of bouncing (show re-routes DRAFTs); a refresh
// resets the beat.
const SUBMITTED_AT_BY_INSTANCE_ID = new Map<string, string>();

/** Clears the page-lifetime submitted-program state (test isolation). */
export function resetDemoTasProgramSubmissions(): void {
  SUBMITTED_AT_BY_INSTANCE_ID.clear();
}

// The approval + admin application-show surfaces walk
// instance.employee(.person/.company/.tasParticipant), so each instance links
// the demo employee; empty hasMany linkage keeps asset/attachment readers on
// a clean empty state instead of crashing on undefined. The walkthrough
// program's instance is PROGRAM_APPROVED (it carries the seeded draft +
// submitted applications); every other program's instance is a DRAFT — the
// state a freshly-started program has, so `apply` lands on the program form
// (programs/show bounces isDraft instances to programs/new).
function buildInstanceResource(
  program: DemoTaProgram,
  identity: DemoFixtureIdentity,
  firstLogin = false
): JsonApiResource {
  const isPrimary = program.id === DEMO_TA_PRIMARY_TEMPLATE_ID;
  const instanceId = instanceIdFor(program.id);
  const submittedAt = SUBMITTED_AT_BY_INSTANCE_ID.get(instanceId);
  const requestApprovalEntry = (timestamp: string) => ({
    event: 'request_program_approval',
    from: 'TAS.ProgramInstanceState.DRAFT',
    to: 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
    timestamp,
    reason: null,
  });
  // One lifecycle decision drives state AND log, so they can never disagree.
  const lifecycle = submittedAt
    ? {
        state: 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
        transitionLog: [requestApprovalEntry(submittedAt)],
      }
    : isPrimary && !firstLogin
      ? {
          state: 'TAS.ProgramInstanceState.PROGRAM_APPROVED',
          transitionLog: [
            requestApprovalEntry('2026-05-10T14:00:00Z'),
            {
              event: 'approve_program',
              from: 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
              to: 'TAS.ProgramInstanceState.PROGRAM_APPROVED',
              timestamp: '2026-05-12T14:00:00Z',
              reason: null,
            },
          ],
        }
      : { state: 'TAS.ProgramInstanceState.DRAFT', transitionLog: [] };
  return {
    type: 'tas-program-instance',
    id: instanceId,
    attributes: {
      state: lifecycle.state,
      // The program details page filters instance.customFields unguarded, and
      // the progress tracker walks instance.transitionLog.some(...) unguarded
      // — both must exist (empty is fine for a just-started draft).
      customFields: [],
      // The real create copies the applicant fields onto the instance; the
      // wizard renders them required-but-disabled, so they must carry values.
      fields: {
        NAME: { values: [`${identity.firstName} ${identity.lastName}`] },
        EMPLOYEE_ID: { values: ['EMP-2001'] },
        EMPLOYEE_EMAIL: { values: [demoEmail(identity)] },
        FT_PT_OTHER: { values: ['Full-Time'] },
      },
      // Log EVENTS are snake_case on the wire — programSubmissionDate finds
      // 'request_program_approval' (tas-program-instance.ext), NOT the
      // dasherized transition-endpoint slug.
      transitionLog: lifecycle.transitionLog,
    },
    relationships: {
      tasProgramTemplate: { data: { type: 'tas-program-template', id: program.id } },
      employee: { data: { type: 'employee', id: 'demo-tas-employee-1' } },
      tasAssets: { data: [] },
      attachments: { data: [] },
      // The program show page reads instance.tasApplications.length unguarded;
      // the walkthrough instance links its in-flight application (sideloaded by
      // the doc builders), the startable drafts have none yet.
      tasApplications: {
        data:
          isPrimary && !firstLogin
            ? [{ type: 'tas-application', id: 'demo-tas-application-1' }]
            : [],
      },
    },
  };
}

const PARTICIPANT_RESOURCE: JsonApiResource = {
  type: 'tas-participant',
  id: 'demo-tas-participant-1',
  attributes: { state: 'TAS.ParticipantState.ACTIVE', employmentStartDate: '2022-01-15' },
  relationships: {
    tasEligibilities: {
      data: ELIGIBILITY_RESOURCES.map((eligibility) => ({
        type: 'tas-eligibility',
        id: eligibility.id as string,
      })),
    },
    tasGroupings: { data: [] },
  },
};

// Course expenses in CENTS; they sum to $1,466.45, the walkthrough's requested
// total. Under prepayment the tuition is the CourseCash-covered part
// (CARD_PRE_APPROVED_TOTAL) while books/fees remain reimbursable, so the
// approval page shows the real CourseCash-vs-reimbursement split.
const COURSE_TUITION_CENTS = 118_450; // $1,184.50
const COURSE_BOOKS_CENTS = 21_375; // $213.75
const COURSE_FEES_CENTS = 6_820; // $68.20

// Mirrors the snapshot plane's per-model naming (reimagined-data
// getDemoTasData: 'Coursera / Google (CourseCash)' / 'Riverview LPN School
// (deferral-eligible)'), so the employee reimagined pages and the store-routed
// drill-ins narrate the same demo.
function courseNameFor(paymentModelId: DemoTaPaymentModelId): string {
  switch (paymentModelId) {
    case 'prepayment':
      return 'Google Data Analytics (CourseCash)';
    case 'reimbursement-with-deferral':
      return 'LPN to RN Bridge — Riverview LPN School (deferral-eligible)';
    case 'reimbursement':
      return 'Intro to Data Science';
    default:
      return assertNever(paymentModelId);
  }
}

// The expense table, requested totals, and the CourseCash subtotal on the
// approval page all derive from tas-course COURSE_* fields (fields.REQUESTED_TOTAL
// on the application has no model reader) — without a course every amount
// renders zero. The course's tasApplication back-link is deliberately omitted:
// the inverse is wired when the application's `tasCourses` linkage is pushed,
// which also stays correct when the handler echoes a requested application id.
function buildCourseResource(paymentModelId: DemoTaPaymentModelId): JsonApiResource {
  return {
    type: 'tas-course',
    id: 'demo-tas-course-1',
    attributes: {
      isActive: true,
      customFields: [],
      fields: {
        COURSE_NAME: { values: [courseNameFor(paymentModelId)] },
        COURSE_NUMBER: { values: ['DEMO-101'] },
        COURSE_TUITION: { values: [COURSE_TUITION_CENTS] },
        COURSE_BOOKS: { values: [COURSE_BOOKS_CENTS] },
        COURSE_FEES: { values: [COURSE_FEES_CENTS] },
        COURSE_LABS: { values: [0] },
        COURSE_SOFTWARE: { values: [0] },
        COURSE_CREDIT: { values: [3] },
      },
    },
    relationships: {
      tasAssets: { data: [] },
      attachments: { data: [] },
    },
  };
}

// The admin financial summary's CourseCash section reads the application's
// pcsCards for the card-status chip. CREATED renders "Pending Activation",
// coherent with a pending-approval application. The employee card display
// (stripe-card-display) guards its Stripe issuing-elements setup behind
// card.pcsCompany.providerId, which this resource deliberately omits, so demo
// never reaches the issuing flow or its raw `/pcs-cards/:id/client-token`
// fetch. The tasApplication back-link is inverse-wired from the application.
const PCS_CARD_RESOURCE: JsonApiResource = {
  type: 'pcs-card',
  id: 'demo-pcs-card-1',
  attributes: {
    state: 'PCS.CardState.CREATED',
    program: 'PCS.Program.COURSE_CASH',
    amounts: {
      authorization: {
        approved: 0,
        captured: 0,
        created: 0,
        declined: 0,
        expired: 0,
        requested: 0,
        reversed: 0,
      },
      dispute: { created: 0 },
      program: { approved: COURSE_TUITION_CENTS },
      transaction: { captured: 0, created: 0, refunded: 0 },
    },
  },
  relationships: {
    pcsAuthorizations: { data: [] },
    pcsCardEvents: { data: [] },
    pcsDisputes: { data: [] },
    pcsTransactions: { data: [] },
  },
};

// Application states used by the walkthrough: the employee CONTINUES a
// DEFINE_COURSES draft and submits it; the approver APPROVES a matching
// PENDING_COURSES_APPROVAL application in their queue.
const STATE_DRAFT = 'TAS.ApplicationState.DEFINE_COURSES';
const STATE_PENDING = 'TAS.ApplicationState.PENDING_COURSES_APPROVAL';

// Every async:false hasMany the approval screens iterate is linked empty —
// an OMITTED linkage materializes as undefined and crashes `.find(...)` /
// `.filter(...)` readers, while an empty one renders the empty state.
function buildApplicationResource(
  paymentModelId: DemoTaPaymentModelId,
  state: string = STATE_PENDING
): JsonApiResource {
  const isPrepayment = paymentModelId === 'prepayment';
  const isDraft = state === STATE_DRAFT;

  return {
    type: 'tas-application',
    id: 'demo-tas-application-1',
    attributes: {
      state,
      // No pre-approval payments exist; TODO: add richer deferral payment states to the approved TAS demo scenario.
      paymentState: 'TAS.ApplicationPaymentState.NONE',
      fields: {
        // The guided wizard's step-1 completeness gate requires APPLICATION_NAME
        // (hardcoded required in step-education-request); without it the draft
        // opens on an empty step 1 instead of walking straight to Submit.
        APPLICATION_NAME: { values: [`Fall 2026 — ${courseNameFor(paymentModelId)}`] },
        COURSE_NAME: { values: [courseNameFor(paymentModelId)] },
        REQUESTED_TOTAL: { values: [1500] },
        ...(isPrepayment
          ? {
              // model.paymentMethod — flips every CourseCash gate on the
              // approval surfaces.
              PAYMENT_TYPE_FOR_DIRECT_PAY: { values: ['TAS.AssistanceManner.COURSE_CASH'] },
              // model.cardPreApprovedTotal, in CENTS (the surfaces format /100).
              CARD_PRE_APPROVED_TOTAL: { values: [COURSE_TUITION_CENTS] },
            }
          : {}),
      },
      customFields: [],
      // ApplicationAmounts in CENTS, fully formed for EVERY model:
      // stripe-card-display reads amounts.course_cash UNGUARDED, so the block
      // must exist the moment a pcs-card makes that component render.
      amounts: {
        requested: COURSE_TUITION_CENTS + COURSE_BOOKS_CENTS + COURSE_FEES_CENTS,
        approved_for_payment: 0,
        course_cash: { reserved: 0, used: 0, refunded: 0 },
        pending_payment: 0,
        paid: 0,
        refund_requested: 0,
        refund_paid_back: 0,
        refund_forgiven: 0,
        refund_cancelled: 0,
        refund_limit: 0,
        flags: { request_payment_enabled: false, request_repayment_enabled: false },
      },
      // The approval-queue status widgets read the state-matching approval
      // summary (`coursesApproval.possible_reviewers` etc.) without guarding.
      coursesApproval: {
        affirmed: [],
        current_reviewers: [],
        possible_reviewers: [],
        required: [],
        approved: false,
      },
      // The approval surfaces derive submission/approval dates from these logs
      // via `.find(...)` — they must exist (empty is fine; undefined crashes).
      // A DEFINE_COURSES draft has not been submitted yet, so it carries no
      // submit event; the pending (approver) application does.
      transitionLog: isDraft
        ? []
        : [
            {
              event: 'submit',
              from: STATE_DRAFT,
              to: STATE_PENDING,
              timestamp: '2026-05-20T14:00:00Z',
              reason: null,
            },
          ],
      paymentStateLog: [],
      approvedTotalLog: [],
    },
    relationships: {
      tasProgramInstance: { data: { type: 'tas-program-instance', id: 'demo-tas-instance-1' } },
      tasCourses: { data: [{ type: 'tas-course', id: 'demo-tas-course-1' }] },
      tasAssistances: { data: [] },
      tasAssets: { data: [] },
      attachments: { data: [] },
      aiRecommendations: { data: [] },
      pcsCards: { data: isPrepayment ? [{ type: 'pcs-card', id: 'demo-pcs-card-1' }] : [] },
      tags: { data: [] },
    },
  };
}

// company-setting drives several dashboard reads
// (currentEmployee.company.companySetting.tuitionAssistance.*). The direct-pay
// and card-review keys are the employer-level CourseCash gates, mirroring
// buildDemoCompanySettings (career-pathways-demo-data.ts), so the store plane
// and the partner-service blob agree on whether CourseCash UI is enabled.
function buildCompanySettingResource(paymentModelId: DemoTaPaymentModelId): JsonApiResource {
  const isPrepayment = paymentModelId === 'prepayment';
  return {
    type: 'company-setting',
    id: 'demo-tas-companysetting-1',
    attributes: {
      tuitionAssistance: {
        hideAnnualBenefitsCard: false,
        isDependentProgramsEnabled: false,
        isDirectPayEnabled: isPrepayment,
        isCardUsageReviewEnabled: isPrepayment,
      },
    },
  };
}

function buildCompanyResource(employerName: string): JsonApiResource {
  return {
    type: 'company',
    id: 'demo-tas-company-1',
    attributes: { name: employerName, isActive: true },
    relationships: {
      companySetting: { data: { type: 'company-setting', id: 'demo-tas-companysetting-1' } },
    },
  };
}

// Employee findRecord (include tas-participant,company) — the dashboard reads
// employee.tasParticipant to decide whether to run eligibility checks, so the
// participant must be sideloaded for the demo program to surface. Its
// company/company-setting/participant ids are reused by demo/identity-fixtures.ts
// (buildDemoIdentityDoc) for the demo session identity — and its NAMES derive
// from the same identity, so this later push can never rename the session user.
function buildEmployeeResource(identity: DemoFixtureIdentity): JsonApiResource {
  return {
    type: 'employee',
    id: 'demo-tas-employee-1',
    attributes: {
      firstName: identity.firstName,
      lastName: identity.lastName,
      isActive: true,
      // Admin application-details reads customAttributes.raw?... without
      // guarding customAttributes itself.
      customAttributes: {},
      employmentType: 'FULL_TIME',
    },
    relationships: {
      tasParticipant: { data: { type: 'tas-participant', id: 'demo-tas-participant-1' } },
      company: { data: { type: 'company', id: 'demo-tas-company-1' } },
    },
  };
}

const participantCollection: JsonApiDoc = {
  data: [PARTICIPANT_RESOURCE],
  included: [...ELIGIBILITY_RESOURCES],
};

// Empty OpenSearch result — a valid "enrolled, no applications yet" state.
// executeSearchRequest is a store.request builder (POST, SkipCache), so the
// demo HANDLER intercepts it (not the fetch interceptor); callers read .content.
const EMPTY_SEARCH = {
  took: 0,
  timedOut: false,
  hits: { total: { value: 0, relation: 'eq' }, maxScore: 0, hits: [] },
  aggregations: {},
};

// --- Admin TAS approval queue (OpenSearch /api/v2/search, model-type
// TasProgramInstance) -------------------------------------------------------
// The admin approval queue (routes/.../admin/tas/approval-queue) POSTs an
// OpenSearch query and renders `OpenSearchResponseBuilder.from(res).toPaginatedResult()`.
// Each hit's `_source` is a TasProgramInstance doc; the queue row reads
// employeeNameKeyword / employeePayrollIdKeyword / companyName / state / dates.
interface QueueAppSource {
  id: string;
  employeeName: string;
  payrollId: string;
  employeeId: number;
  personId: number;
  state: string;
  submitted: string;
  priority: string;
}

// "May 2026" for '2026-05-20' — the shape displayName falls back to (UTC pins date-only strings).
const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const monthYearOf = (isoDate: string) => MONTH_YEAR_FORMAT.format(new Date(`${isoDate}T00:00:00Z`));

function queueHit(app: QueueAppSource, employerName: string, programName: string) {
  return {
    _index: 'tas_program_instances',
    _id: app.id,
    _score: 1,
    _source: {
      id: app.id,
      recordType: 'application',
      inRepayment: false,
      companyId: 'demo-tas-company-1',
      companyName: employerName,
      employeeId: app.employeeId,
      personId: app.personId,
      employeeNameKeyword: app.employeeName,
      employeePayrollIdKeyword: app.payrollId,
      tasApplicationIdKeyword: `${app.id}-application`,
      // Employee benefit-history renders these docs too (getApplicationName);
      // absent, it falls back to "Application #<fixture-id>" — a shape the
      // product never shows. Month-year mirrors displayName's fallback.
      tasApplicationFieldApplicationNameKeyword: monthYearOf(app.submitted),
      tasProgramInstanceIdKeyword: `${app.id}-instance`,
      tasApplicationStateKeyword: app.state,
      tasApplicationCreatedAtDate: app.submitted,
      tasApplicationAttentionPriorityDate: app.priority,
      tasApplicationLatestSubmissionTs: `${app.submitted}T14:00:00Z`,
      tasApplicationLatestStateChangeAtDate: app.submitted,
      tasProgramTemplateFieldProgramNameKeyword: programName,
    },
  };
}

const APPROVAL_QUEUE_APPS: QueueAppSource[] = [
  {
    id: 'demo-queue-1',
    employeeName: 'Jordan Lee',
    payrollId: 'EMP-1001',
    employeeId: 1001,
    personId: 2001,
    state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    submitted: '2026-05-20',
    priority: '2026-05-21',
  },
  {
    id: 'demo-queue-2',
    employeeName: 'Priya Natarajan',
    payrollId: 'EMP-1042',
    employeeId: 1042,
    personId: 2042,
    state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
    submitted: '2026-05-22',
    priority: '2026-05-23',
  },
  {
    id: 'demo-queue-3',
    employeeName: 'Marcus Brown',
    payrollId: 'EMP-1088',
    employeeId: 1088,
    personId: 2088,
    state: 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    submitted: '2026-05-24',
    priority: '2026-05-25',
  },
];

const WAITING_ON_OTHERS_APP: QueueAppSource = {
  id: 'demo-queue-4',
  employeeName: 'Sofia Alvarez',
  payrollId: 'EMP-1112',
  employeeId: 1112,
  personId: 2112,
  state: STATE_PENDING,
  submitted: '2026-05-26',
  priority: '2026-05-27',
};
const decidedApps = () => {
  const now = Date.now();
  const daysAgo = (days: number) => new Date(now - days * 86400000).toISOString();
  return APPROVAL_QUEUE_APPS.slice(0, 2).map((app, index) => ({
    ...app,
    id: `demo-decision-${index + 1}`,
    submitted: daysAgo(index * 2 + 3).slice(0, 10),
    priority: daysAgo(index * 2 + 2).slice(0, 10),
    decidedAt: daysAgo(index + 1),
    responseDays: index + 2,
    state:
      index === 0
        ? 'TAS.ApplicationState.COURSES_APPROVED'
        : 'TAS.ApplicationState.COURSES_REJECTED',
  }));
};

function hasPossibleReviewerFilter(clause: unknown): boolean {
  if (!clause || typeof clause !== 'object') return false;
  return Object.entries(clause).some(
    ([key, value]) => key.includes('possible_reviewers') || hasPossibleReviewerFilter(value)
  );
}

// --- Employee TA activity (the SAME /api/v2/search endpoint, employee-scoped) --
// Employee surfaces scope these searches with an employee_id term filter; the
// admin approval queue doesn't — that structural difference discriminates.

interface OpenSearchBody {
  from?: number;
  size?: number;
  aggs?: Record<string, unknown>;
  query?: { bool?: { filter?: unknown[]; should?: unknown[] } };
}
interface TasSearchRequestBody {
  'model-type'?: string;
  searchBody?: OpenSearchBody;
  'search-body'?: OpenSearchBody;
}

// Callers of executeSearchRequest spell the body key both ways.
const searchBodyOf = (body: unknown) => {
  const request = body as TasSearchRequestBody | undefined;
  return request?.searchBody ?? request?.['search-body'];
};

const searchFilterClauses = (body: unknown) => {
  const filter = searchBodyOf(body)?.query?.bool?.filter;
  return Array.isArray(filter) ? filter : [];
};

// Only the payments route builds a top-level `bool.should` (positive-payment
// ranges); the other employee searches nest their OR blocks inside `filter`.
const hasTopLevelShould = (body: unknown) => {
  const should = searchBodyOf(body)?.query?.bool?.should;
  return Array.isArray(should) && should.length > 0;
};

const hasTermFilterOn = (clause: unknown, field: string) => {
  if (typeof clause !== 'object' || clause === null) {
    return false;
  }
  const term = (clause as { term?: unknown }).term;
  return typeof term === 'object' && term !== null && field in term;
};

const isTasInstanceSearch = (body: unknown) =>
  (body as TasSearchRequestBody | undefined)?.['model-type'] === 'TasProgramInstance';

const isInstitutionSearch = (body: unknown) =>
  (body as TasSearchRequestBody | undefined)?.['model-type'] === 'TasUnifiedInstitution';

// The wizard's school typeahead (TasUnifiedInstitution searches): a fixed
// catalog answers every term so the required picker is always completable.
// Envelope with the total derived from the hits, so counts can never drift.
const openSearchResponse = <T>(hits: T[]) => ({
  took: 0,
  timedOut: false,
  hits: { total: { value: hits.length, relation: 'eq' }, maxScore: 1, hits },
  aggregations: {},
});

function decisionSearchResponse(body: unknown, programName: string) {
  const apps = decidedApps();
  const responseDays = apps.map((app) => app.responseDays).sort((left, right) => left - right);
  const medianResponseDays =
    ((responseDays[Math.floor((responseDays.length - 1) / 2)] ?? 0) +
      (responseDays[Math.floor(responseDays.length / 2)] ?? 0)) /
    2;
  const query = searchBodyOf(body);
  const hits = apps.map((app, index) => ({
    _id: `${app.id}-log`,
    _score: 1,
    _source: {
      id: `${app.id}-log`,
      tasEventTransitionEmployeeNameKeyword: app.employeeName,
      tasEventTransitionProgramTemplateNameKeyword: programName,
      tasEventTransitionDecisionKeyword: index === 0 ? 'approve' : 'reject',
      tasEventTransitionEventTimestampDate: app.decidedAt,
      tasEventTransitionResponseDaysFloat: app.responseDays,
      tasEventTransitionApplicationIdKeyword: `${app.id}-application`,
    },
  }));
  const response = openSearchResponse(hits);
  const from = query?.from ?? 0;
  return {
    ...response,
    hits: { ...response.hits, hits: hits.slice(from, from + (query?.size ?? hits.length)) },
    aggregations:
      query?.size === 0 && query.aggs?.['week'] && query.aggs?.['window']
        ? {
            week: { docCount: apps.length },
            window: {
              docCount: apps.length,
              median: {
                values: [
                  {
                    key: 50,
                    value: medianResponseDays,
                  },
                ],
              },
            },
          }
        : {},
  };
}

const DEMO_INSTITUTION_SEARCH = openSearchResponse(
  [
    ['dapip_9001', 9001, 'Coursera / Google', false],
    ['dapip_9002', 9002, 'Riverview LPN School (deferral-eligible)', true],
    ['dapip_9003', 9003, 'University of North Carolina at Chapel Hill', false],
    ['dapip_9004', 9004, 'Wake Technical Community College', false],
  ].map(([identifier, dapipId, locationName, lpn], index) => ({
    _index: 'tas_unified_institutions',
    _id: identifier,
    _score: 1,
    _source: {
      identifier,
      dapipId,
      institutionId: null,
      locationName,
      locationType: 'Institution',
      address: 'Remote / Online',
      instIsFeatured: index === 0,
      instIsPreferred: index === 0,
      lpn,
    },
  }))
);

const isEmployeeScopedSearch = (body: unknown) =>
  searchFilterClauses(body).some((clause) => hasTermFilterOn(clause, 'employee_id'));

// fetchDependentTasData adds a `must_not` on the dependent-id keyword; a demo
// has no dependent programs, so those searches fall to the empty fixture.
const isDependentScopedSearch = (body: unknown) =>
  searchFilterClauses(body).some((clause) => {
    const mustNot = (clause as { bool?: { must_not?: unknown[] } }).bool?.must_not;
    return (
      Array.isArray(mustNot) &&
      mustNot.some((inner) => hasTermFilterOn(inner, 'tas_program_instance_dependent_id_keyword'))
    );
  });

// One authored application per activity row. Money fields are CENTS; omitted
// payment fields derive real wire behavior (no paid amount -> NONE).
interface ActivityAppSource {
  /** JSON:API application id — demo-app-* so the show-route proxy resolves clicks. */
  id: string;
  state: string;
  /** Date-only ISO; the row title renders month-year of this date. */
  createdAt: string;
  updatedAt: string;
  coursesBegin: string;
  coursesEnd: string;
  requestedCents?: number;
  approvedCents?: number;
  paidCents?: number;
  cardPreApprovedCents?: number;
  cardFinalApprovedCents?: number;
  courseCashUsedCents?: number;
  courseCashReservedCents?: number;
}

function activityHit(
  app: ActivityAppSource,
  identity: DemoFixtureIdentity,
  programName: string,
  institution: string,
  major: string,
  instanceState: string
) {
  return {
    _index: 'tas_program_instances',
    _id: app.id,
    _score: 1,
    _source: {
      id: app.id,
      recordType: 'application',
      inRepayment: false,
      companyId: 'demo-tas-company-1',
      companyName: identity.employerName,
      employeeNameKeyword: `${identity.firstName} ${identity.lastName}`,
      tasApplicationIdKeyword: app.id,
      tasApplicationFieldApplicationNameKeyword: monthYearOf(app.createdAt),
      tasApplicationStateKeyword: app.state,
      tasApplicationCreatedAtDate: app.createdAt,
      tasApplicationFieldCoursesBeginDateDate: app.coursesBegin,
      tasApplicationFieldCoursesEndDateDate: app.coursesEnd,
      updatedAt: app.updatedAt,
      ...(app.requestedCents !== undefined && {
        tasApplicationFieldPreApprovedTotalInteger: app.requestedCents,
      }),
      ...(app.approvedCents !== undefined && {
        tasApplicationPaymentFinalApprovedTotalAmountInteger: app.approvedCents,
      }),
      ...(app.paidCents !== undefined && {
        tasApplicationPaymentPaidAmountInteger: app.paidCents,
        // A paid application always carries its last payment date on the wire.
        tasApplicationPaymentLastDateDate: app.updatedAt,
      }),
      ...(app.cardPreApprovedCents !== undefined && {
        tasApplicationFieldCardPreApprovedTotalInteger: app.cardPreApprovedCents,
      }),
      ...(app.cardFinalApprovedCents !== undefined && {
        tasApplicationFieldCardFinalApprovedTotalInteger: app.cardFinalApprovedCents,
      }),
      ...(app.courseCashUsedCents !== undefined && {
        tasApplicationCourseCashUsedAmountInteger: app.courseCashUsedCents,
      }),
      ...(app.courseCashReservedCents !== undefined && {
        tasApplicationCourseCashReservedAmountInteger: app.courseCashReservedCents,
      }),
      tasProgramInstanceIdKeyword: 'demo-tas-instance-1',
      tasProgramInstanceStateKeyword: instanceState,
      tasProgramInstanceCreatedAtDate: '2025-09-01',
      tasProgramInstanceFieldSchoolInstitutionNameKeyword: institution,
      tasProgramInstanceFieldProgramMajorKeyword: major,
      tasProgramTemplateIdKeyword: DEMO_TA_PRIMARY_TEMPLATE_ID,
      tasProgramTemplateFieldProgramNameKeyword: programName,
      tasProgramTemplateTypeKeyword: 'TAS.ProgramType.4.2',
    },
  };
}

/** The walkthrough amounts, in cents: $1,466.45 approved, $733.23 reimbursed so far. */
const WALKTHROUGH_APPROVED_CENTS = 146_645;
const WALKTHROUGH_PAID_CENTS = 73_323;
const COMPLETED_TOTAL_CENTS = 294_780;
/** The shared demo plan ceiling, converted to the cents these fixtures speak. */
const DEMO_TAS_ANNUAL_LIMIT_CENTS = DEMO_TAS_ANNUAL_LIMIT_DOLLARS * 100;

/**
 * The employee's TA activity per scenario, as the OpenSearch documents the
 * live transform consumes — the scenario axis enters at the wire, and the
 * real mapping/derivation code shapes everything rendered.
 *
 * Payment-model projections are wire facts: CourseCash reports card
 * used/reserved; deferral pays nothing until FULFILLED.
 */
export function buildEmployeeTasActivityDocs(
  identity: DemoFixtureIdentity,
  scenarioId: DemoTasScenarioId,
  paymentModelId: DemoTaPaymentModelId,
  benefitsMaxedOut: boolean
) {
  const isPrepayment = paymentModelId === 'prepayment';
  const isDeferral = paymentModelId === 'reimbursement-with-deferral';
  const institution = isDeferral
    ? 'Riverview LPN School (deferral-eligible)'
    : isPrepayment
      ? 'Coursera / Google (CourseCash)'
      : 'Coursera / Google';
  const major = isDeferral ? 'Nursing' : 'Data Analytics';
  const programName =
    getDemoTaProgramsForClient(identity.clientId).find(
      (program) => program.id === DEMO_TA_PRIMARY_TEMPLATE_ID
    )?.name ?? 'Tuition Reimbursement';

  const baseDates = {
    createdAt: '2025-08-15',
    updatedAt: '2026-01-20',
    coursesBegin: '2025-09-01',
    coursesEnd: '2025-12-15',
  };

  // The base application, projected per payment model: reimbursement is half
  // reimbursed; CourseCash has spent the card; deferral has paid nothing yet.
  const attendApp: ActivityAppSource = {
    id: 'demo-app-1',
    state: 'TAS.ApplicationState.ATTEND',
    ...baseDates,
    requestedCents: WALKTHROUGH_APPROVED_CENTS,
    approvedCents: WALKTHROUGH_APPROVED_CENTS,
    ...(isPrepayment
      ? {
          paidCents: WALKTHROUGH_APPROVED_CENTS,
          cardPreApprovedCents: WALKTHROUGH_APPROVED_CENTS,
          cardFinalApprovedCents: WALKTHROUGH_APPROVED_CENTS,
          courseCashUsedCents: WALKTHROUGH_APPROVED_CENTS,
          courseCashReservedCents: 0,
        }
      : isDeferral
        ? {}
        : { paidCents: WALKTHROUGH_PAID_CENTS }),
  };

  const scenarioApps: Record<DemoTasScenarioId, ActivityAppSource[]> = {
    'not-started': [],
    'in-progress': [
      attendApp,
      {
        id: 'demo-app-2',
        state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
        createdAt: '2026-01-20',
        updatedAt: '2026-01-20',
        coursesBegin: '2026-02-01',
        coursesEnd: '2026-04-30',
      },
    ],
    approved: [
      {
        ...attendApp,
        state: 'TAS.ApplicationState.COURSES_APPROVED',
        // Approval reserves CourseCash funds — nothing is paid until courses run.
        ...(isPrepayment
          ? {
              paidCents: undefined,
              courseCashUsedCents: 0,
              courseCashReservedCents: WALKTHROUGH_APPROVED_CENTS,
            }
          : { paidCents: undefined }),
      },
    ],
    // Authored fresh (not peeled off attendApp) so no money field can leak in.
    rejected: [
      {
        id: 'demo-app-1',
        state: 'TAS.ApplicationState.COURSES_REJECTED',
        ...baseDates,
        requestedCents: WALKTHROUGH_APPROVED_CENTS,
      },
    ],
    completed: [
      {
        ...attendApp,
        state: 'TAS.ApplicationState.FULFILLED',
        requestedCents: COMPLETED_TOTAL_CENTS,
        approvedCents: COMPLETED_TOTAL_CENTS,
        ...(isPrepayment
          ? {
              paidCents: COMPLETED_TOTAL_CENTS,
              cardPreApprovedCents: COMPLETED_TOTAL_CENTS,
              cardFinalApprovedCents: COMPLETED_TOTAL_CENTS,
              courseCashUsedCents: COMPLETED_TOTAL_CENTS,
              courseCashReservedCents: 0,
            }
          : { paidCents: COMPLETED_TOTAL_CENTS }),
      },
    ],
  };

  const maxed = (cents?: number) => pinToAnnualLimitIfNonzero(cents, DEMO_TAS_ANNUAL_LIMIT_CENTS);
  const apps = benefitsMaxedOut
    ? scenarioApps[scenarioId].map((app) => ({
        ...app,
        requestedCents: maxed(app.requestedCents),
        approvedCents: maxed(app.approvedCents),
        paidCents: maxed(app.paidCents),
        cardPreApprovedCents: maxed(app.cardPreApprovedCents),
        cardFinalApprovedCents: maxed(app.cardFinalApprovedCents),
        courseCashUsedCents: maxed(app.courseCashUsedCents),
        courseCashReservedCents: maxed(app.courseCashReservedCents),
      }))
    : scenarioApps[scenarioId];

  const instanceState =
    scenarioId === 'completed'
      ? 'TAS.ProgramInstanceState.COMPLETED'
      : 'TAS.ProgramInstanceState.IN_PROGRESS';
  return apps.map(
    (app) => activityHit(app, identity, programName, institution, major, instanceState)._source
  );
}

/** The two /api/v2/search fixtures over the activity docs: payments (paid-only rows) + general. */
export function buildEmployeeTasActivitySearchMatchers(
  identity: DemoFixtureIdentity,
  scenarioId: DemoTasScenarioId,
  paymentModelId: DemoTaPaymentModelId,
  benefitsMaxedOut: boolean
): MatcherFixture[] {
  const docs = buildEmployeeTasActivityDocs(identity, scenarioId, paymentModelId, benefitsMaxedOut);
  const searchResponse = (sources: typeof docs) =>
    openSearchResponse(
      sources.map((source) => ({
        _index: 'tas_program_instances',
        _id: source.id,
        _score: 1,
        _source: source,
      }))
    );
  const isEmployeeActivitySearch = (body: unknown) =>
    isTasInstanceSearch(body) && isEmployeeScopedSearch(body) && !isDependentScopedSearch(body);

  return [
    // Payments first (first-match-wins): only applications with a paid amount.
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: matchesSearchEndpoint,
        bodyMatches: (body) => isEmployeeActivitySearch(body) && hasTopLevelShould(body),
      },
      content: searchResponse(
        docs.filter((source) => 'tasApplicationPaymentPaidAmountInteger' in source)
      ),
    },
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: matchesSearchEndpoint,
        bodyMatches: isEmployeeActivitySearch,
      },
      content: searchResponse(docs),
    },
  ];
}

export function buildInProgressTasMatchers(
  identity: DemoFixtureIdentity,
  paymentModelId: DemoTaPaymentModelId,
  options: { includeApprovalQueue: boolean; firstLogin?: boolean }
): MatcherFixture[] {
  // The client's program catalog (industry-aware slot 3). The FIRST program is
  // the walkthrough one — the seeded draft + submitted applications, the
  // approval queue, and the approved instance all hang off it.
  const catalog = getDemoTaProgramsForClient(identity.clientId);
  const templateResources = catalog.map(buildTemplateResource);
  const instanceResources = catalog.map((program) =>
    buildInstanceResource(program, identity, options.firstLogin)
  );
  const templateResource = templateResources[0] as JsonApiResource;
  const primaryInstance = instanceResources[0] as JsonApiResource;
  const programName = catalog[0]?.name ?? 'Tuition Reimbursement';
  const companyResource = buildCompanyResource(identity.employerName);
  const companySettingResource = buildCompanySettingResource(paymentModelId);
  const employeeResource = buildEmployeeResource(identity);
  // Op-seam reads (classic admin drill-in, list reads) get the submitted
  // (PENDING) application. The employee's DEFINE_COURSES DRAFT and the approver's
  // PENDING queue applications are served — split by id — on the fetch seam
  // (buildInProgressTasFetchMatchers), which is where the shipped detail /
  // continue-draft / approval routes actually read.
  const applicationResource = buildApplicationResource(paymentModelId);
  const courseResource = buildCourseResource(paymentModelId);

  const employeeDoc: JsonApiDoc = {
    data: employeeResource,
    included: [
      PARTICIPANT_RESOURCE,
      ...ELIGIBILITY_RESOURCES,
      companyResource,
      companySettingResource,
    ],
  };
  const templateCollection: JsonApiDoc = {
    data: [...templateResources],
    included: [companyResource, companySettingResource],
  };
  const templateDoc: JsonApiDoc = {
    data: templateResource,
    included: [companyResource, companySettingResource],
  };
  // The instance links the demo employee, so every doc that sideloads it also
  // sideloads the employee graph (participant/eligibility/company/setting) —
  // async:false relationships resolve empty unless included.
  const employeeGraph = [
    employeeResource,
    PARTICIPANT_RESOURCE,
    ...ELIGIBILITY_RESOURCES,
    companyResource,
    companySettingResource,
  ];
  // ACTIVE-instance queries serve ONLY the walkthrough program's approved
  // instance. The other programs' DRAFT instances exist solely behind their
  // per-id reads (they materialize when apply-for-assistance returns their id)
  // — listing them here would make every catalog card read as "already
  // enrolled" and kill the start-application guards.
  const progressGraph = options.firstLogin ? [] : [applicationResource, courseResource];
  const instanceCollection: JsonApiDoc = {
    data: options.firstLogin ? [] : [primaryInstance],
    included: options.firstLogin ? [] : [templateResource, ...employeeGraph, ...progressGraph],
  };
  const instanceDoc: JsonApiDoc = {
    data: primaryInstance,
    included: [templateResource, ...employeeGraph, ...progressGraph],
  };
  // Sideload the instance AND its template so async:false
  // tasProgramInstance.tasProgramTemplate resolves on the application show/confirmation routes.
  // The course (and the CourseCash card under prepayment) ride along for the
  // same reason — the expense table and card chip read them async:false.
  const applicationGraph = [
    courseResource,
    ...(paymentModelId === 'prepayment' ? [PCS_CARD_RESOURCE] : []),
    primaryInstance,
    templateResource,
    ...employeeGraph,
  ];
  const applicationDoc: JsonApiDoc = {
    data: applicationResource,
    included: applicationGraph,
  };
  // Application list reads (e.g. the employee's applications on the admin
  // drill-in) get the same in-progress application.
  const applicationCollection: JsonApiDoc = {
    data: options.firstLogin ? [] : [applicationResource],
    included: options.firstLogin ? [] : applicationGraph,
  };

  // Built lazily — employee-role sessions exclude the queue entirely, and this
  // builder runs on every intercepted request.
  const approvalQueueMatcher = (): MatcherFixture => ({
    matcher: {
      kind: 'builder',
      method: 'POST',
      urlMatches: matchesSearchEndpoint,
      bodyMatches: (body) => isTasInstanceSearch(body) && !isEmployeeScopedSearch(body),
    },
    contentFor: (body) =>
      openSearchResponse(
        (hasPossibleReviewerFilter(searchBodyOf(body))
          ? [WAITING_ON_OTHERS_APP]
          : APPROVAL_QUEUE_APPS
        ).map((app) => queueHit(app, identity.employerName, programName))
      ),
  });

  return [
    // Approver/admin only (employee reads would 403 live); precedes the empty-search matcher.
    ...(options.includeApprovalQueue ? [approvalQueueMatcher()] : []),
    ...(options.includeApprovalQueue
      ? [
          {
            matcher: {
              kind: 'builder' as const,
              method: 'POST',
              urlMatches: matchesSearchEndpoint,
              bodyMatches: (body: unknown) =>
                (body as TasSearchRequestBody | undefined)?.['model-type'] === 'TasEventTransition',
            },
            contentFor: (body: unknown) => decisionSearchResponse(body, programName),
          },
        ]
      : []),
    // The wizard's school typeahead — must precede the generic empty search.
    {
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlMatches: matchesSearchEndpoint,
        bodyMatches: isInstitutionSearch,
      },
      content: DEMO_INSTITUTION_SEARCH,
    },
    // Base /api/v2/search POSTs only — never the /api/v2/search/* subroutes.
    {
      matcher: { kind: 'builder', method: 'POST', urlMatches: matchesSearchEndpoint },
      content: EMPTY_SEARCH,
    },
    // "Start application": initializeTypedProgramRequest POSTs
    // /tas-program-templates/:id/apply-for-assistance (SkipCache builder) and
    // reads the created instance id off `content.data.id`. One matcher per
    // catalog program returns that program's own draft instance, so every
    // eligible-benefits card starts into its own program form instead of
    // erroring on the network-free 204.
    ...catalog.map((program): MatcherFixture => ({
      matcher: {
        kind: 'builder',
        method: 'POST',
        urlIncludes: `/tas-program-templates/${program.id}/apply-for-assistance`,
      },
      content: { data: { id: instanceIdFor(program.id), type: 'tas-program-instance' } },
    })),
    // Program transition history (programs/history + drill-ins):
    // getTasProgramInstanceHistoryRequest is a SkipCache GET builder — served
    // here so the history page maps over clean empty logs instead of crashing
    // on the inert block body.
    {
      matcher: {
        kind: 'builder',
        method: 'GET',
        urlMatches: (url) => /\/tas-program-instances\/[^/]+\/history(?:\?|$)/.test(url),
      },
      content: { data: { assistance_history: [], application_history: [] } },
    },
    // TA Mentoring request form (tas/mentoring): submitTasCoachingRequest POSTs
    // /coaching/schedule-tas (SkipCache builder). A matched empty body resolves
    // the task so the form shows its success state instead of a submit error.
    {
      matcher: { kind: 'builder', method: 'POST', urlIncludes: '/coaching/schedule-tas' },
      content: {},
    },
    { matcher: { kind: 'op', op: 'findRecord', type: 'employee' }, docs: [employeeDoc] },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-participant' },
      docs: [participantCollection],
    },
    // The dashboard runs a SECOND tas-program-template query for trial templates
    // (admins only). It must NOT return the launched demo program, or the eligible
    // list shows a duplicate card. This filter-specific matcher precedes the
    // generic one below (first match wins) and serves an empty collection.
    {
      matcher: {
        kind: 'op',
        op: 'query',
        type: 'tas-program-template',
        where: (query) => (query?.filter as { isTrial?: boolean } | undefined)?.isTrial === true,
      },
      docs: [{ data: [] }],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-program-template' },
      docs: [templateCollection],
    },
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-template' },
      docs: [templateDoc],
    },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-program-instance' },
      docs: [instanceCollection],
    },
    // tas/programs loads its primary model via findRecord('tas-program-instance', ...).
    {
      matcher: { kind: 'op', op: 'findRecord', type: 'tas-program-instance' },
      docs: [instanceDoc],
    },
    { matcher: { kind: 'op', op: 'query', type: 'tas-group' }, docs: [{ data: [] }] },
    { matcher: { kind: 'op', op: 'findRecord', type: 'tas-application' }, docs: [applicationDoc] },
    {
      matcher: { kind: 'op', op: 'query', type: 'tas-application' },
      docs: [applicationCollection],
    },
  ];
}

// --- Raw-fetch (seam B) matchers -------------------------------------------
// The TAS dashboard reaches these directly via fetch (bypassing the store):
// limits status and the tas-participant eligibility endpoints. (OpenSearch is
// NOT here — executeSearchRequest is a store.request builder, so it is served
// by the demo handler via the '/api/v2/search' builder matcher above.)

const limitsDetail = (limit: number | null, usage: number) => ({
  annual_limit: limit,
  annual_usage: usage,
  available: limit === null ? null : limit - usage,
  hard: limit !== null,
  limit,
  one_month_limit: null,
  one_month_usage: 0,
  six_month_limit: null,
  six_month_usage: 0,
  taxable_limit: null,
  three_month_limit: null,
  three_month_usage: 0,
  usage,
});

// Amounts are cents, matching application.requestedTotal and the submit-time limits check.
const limitsStatusDocForYear = (year: number, used: number) =>
  limitsStatusContract2Document([
    {
      annual_start_date: `${year}-01-01`,
      annual_end_date: `${year}-12-31`,
      available: DEMO_TAS_ANNUAL_LIMIT_CENTS - used,
      available_with_override: DEMO_TAS_ANNUAL_LIMIT_CENTS - used,
      details: {
        company: limitsDetail(null, 0),
        product: limitsDetail(DEMO_TAS_ANNUAL_LIMIT_CENTS, used),
        batch: limitsDetail(null, 0),
        scheme: limitsDetail(null, 0),
      },
      employee_id: 'demo-tas-employee-1',
      hard_limit: 'product_annual',
      limit_capacity: 'TAS.LimitCapacity.MONETARY',
      product: 'TA',
      scheme: null,
      soft_limit: 'none',
    },
  ]);

const DEMO_LIMITS_YEAR = 2026;

// Year requested via ?date=YYYY-…; absent means the fixture window's own year.
const yearFromDateParam = (url: string) => {
  const date = /[?&]date=(\d{4})-/.exec(url)?.[1];
  return date ? Number(date) : DEMO_LIMITS_YEAR;
};
// The summary tree feeds both the dashboard and benefit-history cards.
const limitsSummaryDocForPeriod = (startYear: number, used: number, asOfDate: string) => ({
  data: {
    summary: {
      db_limit: null,
      db_annual_limit: null,
      aggregate_limit: null,
      aggregate_annual_limit: DEMO_TAS_ANNUAL_LIMIT_CENTS,
      effective_limit: null,
      effective_annual_limit: DEMO_TAS_ANNUAL_LIMIT_CENTS,
      used,
      annual_used: used,
      remaining: DEMO_TAS_ANNUAL_LIMIT_CENTS - used,
    },
    tree: {
      'demo-company-1': {
        level: 0,
        label: 'Company',
        children: ['TA'],
        eligible: true,
        db_limit: null,
        db_annual_limit: null,
        aggregate_limit: null,
        aggregate_annual_limit: DEMO_TAS_ANNUAL_LIMIT_CENTS,
        effective_limit: null,
        effective_annual_limit: DEMO_TAS_ANNUAL_LIMIT_CENTS,
        used,
        annual_used: used,
        remaining: DEMO_TAS_ANNUAL_LIMIT_CENTS - used,
      },
      TA: {
        level: 1,
        label: 'TA',
        children: [],
        eligible: true,
        db_limit: null,
        db_annual_limit: DEMO_TAS_ANNUAL_LIMIT_CENTS,
        aggregate_limit: null,
        aggregate_annual_limit: null,
        effective_limit: null,
        effective_annual_limit: DEMO_TAS_ANNUAL_LIMIT_CENTS,
        used,
        annual_used: used,
        remaining: DEMO_TAS_ANNUAL_LIMIT_CENTS - used,
        binding_limit: DEMO_TAS_ANNUAL_LIMIT_CENTS,
        binding_used: used,
        binding_period: 'annual',
      },
    },
    meta: {
      employee_id: 'demo-tas-employee-1',
      company_id: 'demo-company-1',
      as_of_date: asOfDate,
      annual_start_date: `${startYear}-01-01`,
      annual_end_date: `${startYear + 1}-01-01`,
    },
  },
});

/**
 * Clone a single-resource doc with the REQUESTED id echoed into the primary
 * resource (and every included back-reference repointed) — the fetch-seam
 * mirror of the request handler's `withEchoedId`. The shipped TAS detail
 * route reaches this seam via a builder findRecord whose ids can come from
 * OUTSIDE this fixture plane (links rendered by the legacy reimagined-data
 * mock plane carry ids like `demo-app-1`), so the fixture must answer under
 * whatever id was asked.
 */
function echoDocId(doc: JsonApiDoc, id: string): JsonApiDoc {
  const clone = structuredClone(doc);
  const data = clone.data as JsonApiResource;
  const originalId = data.id;
  data.id = id;
  for (const resource of clone.included ?? []) {
    for (const rel of Object.values(resource.relationships ?? {})) {
      const linkage = (rel as { data?: unknown }).data;
      const repoint = (l: { type: string; id: string }) => {
        if (l.type === data.type && l.id === originalId) {
          l.id = id;
        }
      };
      if (Array.isArray(linkage)) {
        (linkage as Array<{ type: string; id: string }>).forEach(repoint);
      } else if (linkage) {
        repoint(linkage as { type: string; id: string });
      }
    }
  }
  return clone;
}

/**
 * Fetch-seam matchers for the SHIPPED TAS surfaces (builder reads carry no
 * SkipCache, so they bypass the request handler and land here).
 */
export function buildInProgressTasFetchMatchers(
  identity: DemoFixtureIdentity,
  paymentModelId: DemoTaPaymentModelId,
  firstLogin = false
): FetchMatcher[] {
  const catalog = getDemoTaProgramsForClient(identity.clientId);
  const templateResources = catalog.map(buildTemplateResource);
  const instanceResources = catalog.map((program) =>
    buildInstanceResource(program, identity, firstLogin)
  );
  const templateResource = templateResources[0] as JsonApiResource;
  const primaryInstance = instanceResources[0] as JsonApiResource;
  const companyResource = buildCompanyResource(identity.employerName);
  const companySettingResource = buildCompanySettingResource(paymentModelId);
  const employeeResource = buildEmployeeResource(identity);
  const courseResource = buildCourseResource(paymentModelId);
  const employeeGraph = [
    employeeResource,
    PARTICIPANT_RESOURCE,
    ...ELIGIBILITY_RESOURCES,
    companyResource,
    companySettingResource,
  ];
  const includedGraph = [courseResource, primaryInstance, templateResource, ...employeeGraph];
  // Two variants of the SAME application (same course/program/amounts, so the
  // approver's queue app mirrors what the employee submits): a DEFINE_COURSES
  // draft for the employee's own app, and a PENDING_COURSES_APPROVAL variant
  // for the approver's queue applications.
  const draftDoc: JsonApiDoc = {
    data: buildApplicationResource(paymentModelId, STATE_DRAFT),
    included: includedGraph,
  };
  const queueDocs = [...APPROVAL_QUEUE_APPS, WAITING_ON_OTHERS_APP, ...decidedApps()].map((app) => {
    const [firstName = '', ...lastNameParts] = app.employeeName.split(' ');
    const applicant = { ...identity, firstName, lastName: lastNameParts.join(' ') };
    const instance = structuredClone(primaryInstance);
    instance.attributes = {
      ...instance.attributes,
      fields: {
        NAME: { values: [app.employeeName] },
        EMPLOYEE_ID: { values: [app.payrollId] },
        EMPLOYEE_EMAIL: { values: [demoEmail(applicant)] },
        FT_PT_OTHER: { values: ['Full-Time'] },
      },
    };
    const application = buildApplicationResource(paymentModelId, app.state);
    application.attributes = {
      ...application.attributes,
      fields: {
        ...(application.attributes?.['fields'] as Record<string, unknown>),
        APPLICATION_NAME: { values: [monthYearOf(app.submitted)] },
      },
      transitionLog: [
        {
          event: 'submit',
          from: STATE_DRAFT,
          to: app.state,
          timestamp: `${app.submitted}T14:00:00Z`,
          reason: null,
        },
      ],
      evidenceApproval: {
        affirmed: [],
        current_reviewers: [],
        possible_reviewers: [],
        required: [],
        approved: false,
      },
    };
    const employee = buildEmployeeResource(applicant);
    employee.attributes = { ...employee.attributes, payrollId: app.payrollId };
    const resources = structuredClone([
      application,
      instance,
      employee,
      courseResource,
      PARTICIPANT_RESOURCE,
      ...ELIGIBILITY_RESOURCES,
      ...(paymentModelId === 'prepayment' ? [PCS_CARD_RESOURCE] : []),
    ]);
    const ids = new Map(
      resources.map((resource) => [
        `${resource.type}:${resource.id}`,
        resource.type === 'tas-application'
          ? `${app.id}-application`
          : resource.type === 'tas-program-instance'
            ? `${app.id}-instance`
            : `${app.id}-${resource.id}`,
      ])
    );
    for (const resource of resources) {
      resource.id = ids.get(`${resource.type}:${resource.id}`) ?? resource.id;
      for (const relationship of Object.values(resource.relationships ?? {})) {
        const linkages = Array.isArray(relationship.data)
          ? relationship.data
          : relationship.data
            ? [relationship.data]
            : [];
        for (const linkage of linkages) {
          linkage.id = ids.get(`${linkage.type}:${linkage.id}`) ?? linkage.id;
        }
      }
    }
    const shared = [templateResource, companyResource, companySettingResource];
    return {
      application: {
        data: resources[0] as JsonApiResource,
        included: [...resources.slice(1), ...shared],
      },
      instance: {
        data: resources[1] as JsonApiResource,
        included: [resources[0] as JsonApiResource, ...resources.slice(2), ...shared],
      },
    };
  });
  const queueApplicationsById = new Map(
    queueDocs.map((docs) => [docs.application.data.id, docs.application])
  );
  const queueInstancesById = new Map(
    queueDocs.map((docs) => [docs.instance.data.id, docs.instance])
  );
  // Per-id docs for the catalog templates + their instances — the shipped
  // reads (getting-started's findRecord, programs/show, programs/new) are
  // plain builders that land on THIS seam, and each id must resolve to ITS
  // OWN program (a single echoed doc would render the wrong program name for
  // three of the four cards).
  const templateDocsById = new Map(
    catalog.map((program, index) => [
      program.id,
      {
        data: templateResources[index] as JsonApiResource,
        included: [companyResource, companySettingResource],
      } satisfies JsonApiDoc,
    ])
  );
  const instanceDocsById = new Map(
    catalog.map((program, index) => [
      instanceIdFor(program.id),
      {
        data: instanceResources[index] as JsonApiResource,
        included: [
          templateResources[index] as JsonApiResource,
          ...employeeGraph,
          // The walkthrough instance links its in-flight application
          // (async:false — a dangling linkage renders as a missing record).
          ...(program.id === DEMO_TA_PRIMARY_TEMPLATE_ID && !firstLogin
            ? [draftDoc.data as JsonApiResource, courseResource]
            : []),
        ],
      } satisfies JsonApiDoc,
    ])
  );
  const primaryInstanceDoc = instanceDocsById.get(
    instanceIdFor(DEMO_TA_PRIMARY_TEMPLATE_ID)
  ) as JsonApiDoc;

  return [
    // The wizard's submit: a raw builder PATCH (no op) lands on this seam;
    // record the id so later instance reads serve PENDING_PROGRAM_APPROVAL.
    {
      urlMatches: (url) =>
        /\/tas-program-instances\/[^/?]+\/transition\/state\/request-program-approval(?:\?|$)/.test(
          url
        ),
      method: 'PATCH',
      json: (url: string) => {
        const id = /\/tas-program-instances\/([^/?]+)\/transition/.exec(url)?.[1];
        if (id) {
          SUBMITTED_AT_BY_INSTANCE_ID.set(id, new Date().toISOString());
        }
        return {};
      },
    },
    // Application DETAIL builder read (`/tas-applications/<id>?include=…`,
    // never the /history subroute below). The id is echoed so links carrying
    // legacy mock-plane ids (demo-app-1, demo-queue-*-application) resolve.
    // Approver queue applications (demo-queue-*) resolve PENDING for the approve
    // flow; the employee's own application resolves as the DEFINE_COURSES draft.
    {
      urlMatches: (url) => /\/tas-applications\/[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const id = /\/tas-applications\/([^/?]+)/.exec(url)?.[1];
        const doc = id
          ? (queueApplicationsById.get(id) ??
            queueApplicationsById.get(`${id}-application`) ??
            draftDoc)
          : draftDoc;
        return id ? echoDocId(doc, id) : doc;
      },
    },
    // Program-template SINGLE read (getting-started's live path + admin
    // template views): each catalog id gets its own doc; unknown ids echo the
    // walkthrough template.
    {
      urlMatches: (url) => /\/tas-program-templates\/[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const id = /\/tas-program-templates\/([^/?]+)/.exec(url)?.[1];
        if (!id) {
          return templateDocsById.get(DEMO_TA_PRIMARY_TEMPLATE_ID);
        }
        return (
          templateDocsById.get(id) ??
          echoDocId(templateDocsById.get(DEMO_TA_PRIMARY_TEMPLATE_ID) as JsonApiDoc, id)
        );
      },
    },
    // Program-template COLLECTION (LAUNCHED + eligibility filters): the whole
    // catalog. The trial variant must stay empty or the dashboard's admin
    // merge de-dupes/duplicates cards.
    {
      urlMatches: (url) => /\/tas-program-templates(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) =>
        // Decoded compare: trial-only reads stay empty; isTrial=true,false gets the catalog.
        new URL(url).searchParams.get('filter[isTrial]') === 'true'
          ? { data: [] }
          : {
              data: [...templateResources],
              included: [companyResource, companySettingResource],
            },
    },
    // Program-instance SINGLE read (programs/show + programs/new after a
    // start): the walkthrough instance is PROGRAM_APPROVED; the other
    // programs' instances are DRAFTs (the just-started state). Unknown ids
    // echo the walkthrough instance — this also covers legacy mock-plane ids.
    {
      urlMatches: (url) => /\/tas-program-instances\/[^/?]+(\?|$)/.test(url),
      method: 'GET',
      json: (url: string) => {
        const id = /\/tas-program-instances\/([^/?]+)/.exec(url)?.[1];
        if (!id) {
          return primaryInstanceDoc;
        }
        const queue = queueInstancesById.get(id) ?? queueInstancesById.get(`${id}-instance`);
        return queue
          ? echoDocId(queue, id)
          : (instanceDocsById.get(id) ?? echoDocId(primaryInstanceDoc, id));
      },
    },
    // ACTIVE-instances query (getting-started/add-application guards): ONLY
    // the walkthrough program's approved instance — listing the startable
    // programs' drafts here would flip every card to "already enrolled".
    {
      urlMatches: (url) => /\/tas-program-instances(\?|$)/.test(url),
      method: 'GET',
      json:
        primaryInstanceDoc && !firstLogin
          ? { data: [primaryInstanceDoc.data], included: primaryInstanceDoc.included }
          : { data: [] },
    },
    // Mutually-exclusive group lookup on the start path — no demo groups.
    {
      urlMatches: (url) => /\/tas-groups(\?|$)/.test(url),
      method: 'GET',
      json: { data: [] },
    },
    // The detail route's participant lookup
    // (`/tas-participants?filter[employee]=…&include=tasEligibilities`).
    {
      urlMatches: (url) => /\/tas-participants(\?|$)/.test(url),
      method: 'GET',
      json: { data: [PARTICIPANT_RESOURCE], included: [...ELIGIBILITY_RESOURCES] },
    },
  ];
}

// Eligibility maps are keyed by template CODE — a code missing from either map
// filters that program's card off the dashboard, so both maps cover the whole
// catalog (the industry-aware slot's two variants share one code).
const DYNAMIC_ELIGIBILITY = Object.fromEntries(DEMO_TA_PROGRAM_CODES.map((code) => [code, true]));
const PERIODS_OF_ELIGIBILITY = Object.fromEntries(
  DEMO_TA_PROGRAM_CODES.map((code) => [code, [['2024-01-01', '2027-12-31']]])
);

/** Eligibility, history and limits reads; current-year usage follows the persona and other years start unused. */
export function buildTasSharedFetchMatchers({
  benefitsMaxedOut = false,
  firstLogin = false,
}: {
  benefitsMaxedOut?: boolean;
  firstLogin?: boolean;
} = {}): FetchMatcher[] {
  const annualUsed = firstLogin ? 0 : benefitsMaxedOut ? DEMO_TAS_ANNUAL_LIMIT_CENTS : 187_425;
  return [
    { urlIncludes: '/dynamic-eligibility', json: DYNAMIC_ELIGIBILITY },
    { urlIncludes: '/periods-of-eligibility', json: PERIODS_OF_ELIGIBILITY },
    {
      urlIncludes: '/api/v2/limits/status',
      // A ?date= outside the fixture year (benefit-history's next-window read) gets that year's clean window with zero usage.
      json: (url: string) => {
        const year = yearFromDateParam(url);
        return limitsStatusDocForYear(year, year === DEMO_LIMITS_YEAR ? annualUsed : 0);
      },
    },
    {
      urlIncludes: '/api/v2/limits/summary',
      // History requests the next window through ?date=; spending belongs only to the current fixture year.
      json: (url: string) => {
        const year = yearFromDateParam(url);
        return year === DEMO_LIMITS_YEAR
          ? limitsSummaryDocForPeriod(year, annualUsed, `${year}-07-01`)
          : limitsSummaryDocForPeriod(year, 0, `${year}-01-01`);
      },
    },
    // The adapter loads response.data into the application's history; empty arrays render an empty timeline.
    {
      urlMatches: (url) => /\/tas-applications\/[^/]+\/history(?:\?|$)/.test(url),
      method: 'GET',
      json: { data: { assistance_history: [], application_history: [] } },
    },
  ];
}

/** Default in-progress eligibility, limits and history fixtures. */
export const IN_PROGRESS_TAS_FETCH_MATCHERS = buildTasSharedFetchMatchers();
